import {useContext, useEffect, useMemo, useRef, useState} from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {yupResolver} from "@hookform/resolvers/yup";
import {faCheck, faClose} from "@fortawesome/free-solid-svg-icons";
import {FormProvider, useForm} from "react-hook-form";
import dayjs from "dayjs";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";
import useNotificationEmailPreview from "../../hooks/useNotificationEmailPreview.js";

// Utils
import {formatDate} from "../../utils/helpers.js";
import {
  hasChecksheetFilter as typeHasChecksheetFilter,
  parseNotificationTypeKey
} from "./notificationPreviewUtils.js";

// Components
import Modal from "../../components/Modal.js";
import Help from "../../components/Help.js";
import SearchSelect from "../../components/SearchSelect.js";
import InputNotification from "../../components/form/InputNotification.js";
import {
  InputSelect,
  InputError,
  InputCheck,
  InputRadioGroup
} from "../../components/form/FormInputs.js";
import NotificationEmailPreview from "./NotificationEmailPreview.js";
import ModalNotificationEmailPreview from "./ModalNotificationEmailPreview.js";

// Style
import {voice} from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Button,
  Form,
  FormField,
  FormFieldWrapper,
  Heading,
  Inline,
  Loader,
  NotLoaded,
  Small,
  Text
} from "../../style/components/general.js";

export const extractRecipients = list => Object.values(list).map(({id}) => id);

const NOTIFICATION_TYPES = [
  {
    label: "State Change",
    value: "State"
  },
  {
    label: "State Reminder",
    value: "StateRemind"
  },
  {
    label: "Task Summary",
    value: "Completed"
  },
  {
    label: "Outstanding Tasks",
    value: "Remind"
  },
  {
    label: "Overdue Tasks",
    value: "Overdue"
  },
  {
    label: "Component Disabled",
    value: "ComponentDisabled"
  }
];

const NOTIFICATION_DESC = {
  State: "Email notification sent when a facility state changes.",
  StateRemind: "Recurring email notification sent when a facility state has been applied.",
  Completed:
    "Email notification sent when a task is completed with Unacceptable Parameter violations or includes specified Tagged Fields.",
  OutOfRange:
    "Email notification sent when a task is submitted with values outside acceptable ranges.",
  Remind: "Recurring email notification to indicate tasks due during a given interval.",
  Overdue:
    "Recurring email notification sent at a given interval to indicate checksheets that are overdue and require resolution.",
  ComponentDisabled:
    "Email notification sent when a component in this facility is put into a disabled state."
};

const FREQUENCY_OPTIONS = [
  {label: "Daily", name: "daily"},
  {label: "Weekly", name: "weekly"},
  {label: "Monthly", name: "monthly"},
  {label: "Annually", name: "annual"}
];

const hasFrequency = type => {
  if (type === "Remind" || type === "Overdue" || type === "StateRemind") return true;
  return false;
};

const hasChecksheetFilter = typeHasChecksheetFilter;

// Form fields that render their own error for the selected type; anything else is invisible.
const visibleErrorFields = type => {
  const fields = ["type", "recipients", "save"];
  if (type === "State" || type === "StateRemind") fields.push("state");
  if (hasFrequency(type)) fields.push("frequency", "occurrences");
  if (type === "Completed") fields.push("tags");
  if (hasChecksheetFilter(type)) fields.push("checksheets");
  return fields;
};

// API `scheduled`: default or Checksheet State frequency. Optional sheets never appear in
// Overdue / Outstanding emails.
const isNotificationChecksheet = sheet => !!sheet.scheduled;

// useApi resolves an aborted request (superseded by a newer call) with this string.
const isCanceled = response => response === "canceled";

const formatChecksheetOption = sheet => {
  const name = (sheet.name || "Untitled").toUpperCase();
  const freqName = (sheet.frequency?.name || sheet.frequency?.label || "").toUpperCase();
  let label = freqName ? `${name} (${freqName})` : name;
  if (sheet.external) label = `${label} (EXTERNAL)`;
  return {
    id: sheet.id,
    name,
    label,
    external: !!sheet.external,
    frequency: sheet.frequency || null
  };
};

const defaultValues = {
  notification: {
    type: null,
    includeTags: false,
    tags: [],
    filterChecksheets: false,
    checksheets: [],
    frequency: null,
    occurrences: [],
    recipients: []
  }
};

const schema = yup.object().shape({
  notification: yup.object().shape({
    type: yup.string().nullable().required("Please provide notification type."),
    state: yup
      .string()
      .nullable()
      .when("type", {
        is: type => type === "State" || type === "StateRemind",
        then: () => yup.string().required("Please provide a target state.")
      }),
    includeTags: yup.boolean(),
    tags: yup
      .array()
      .nullable()
      // Tags only exist for Task Summary; a leftover includeTags must not block other types.
      .when(["includeTags", "type"], {
        is: (includeTags, type) => includeTags && type === "Completed",
        then: () =>
          yup
            .array()
            .of(yup.object())
            .test({
              test: arr => Array.isArray(arr) && arr.length > 0,
              message: "Please select tags."
            })
      }),
    frequency: yup
      .mixed()
      .nullable()
      .when("type", {
        is: type => hasFrequency(type),
        then: () => yup.string().nullable().required("Please provide frequency.")
      }),
    occurrences: yup
      .mixed()
      .nullable()
      .when("type", {
        is: type => hasFrequency(type),
        then: () =>
          yup
            .array()
            .of(
              yup.object().shape({
                dayOfYear: yup
                  .mixed()
                  .nullable()
                  .when("frequency", {
                    is: freq => freq === "annually",
                    then: () => yup.date().required("Please provide date.")
                  }),
                dayOfMonth: yup
                  .mixed()
                  .nullable()
                  .when("frequency", {
                    is: freq => freq === "monthly",
                    then: () => yup.date().required("Please provide date.")
                  }),
                dayOfWeek: yup
                  .mixed()
                  .nullable()
                  .when("frequency", {
                    is: freq => freq === "weekly",
                    then: () => yup.string().required("Please provide weekday.")
                  }),
                time: yup
                  .mixed()
                  .nullable()
                  .when("frequency", {
                    is: freq => !!freq,
                    then: () => yup.string().time().required("Please provide time.")
                  })
              })
            )
            .test({
              test: (arr, ctx) => {
                if (!Array.isArray(arr) || arr.length === 0) return false;
                for (let i = 0; i < arr.length; i++) {
                  const item = arr[i];
                  if (ctx.parent.type === "anually" && !item.date) return false;
                  if (ctx.parent.type === "monthly" && !item.day) return false;
                  if (ctx.parent.type === "weekly" && !item.weekday) return false;
                  if (!item.time) return false;
                }
                return true;
              },
              message: "Please provide recurrence info."
            })
      }),
    checksheets: yup
      .array()
      .nullable()
      .when(["filterChecksheets", "type"], {
        is: (filterChecksheets, type) => filterChecksheets && hasChecksheetFilter(type),
        then: () =>
          yup
            .array()
            .of(yup.object())
            .test({
              test: arr => Array.isArray(arr) && arr.length > 0,
              message: "Please select at least one checksheet."
            })
      }),
    recipients: yup
      .array()
      .of(yup.object())
      .test({
        test: arr => Array.isArray(arr) && arr.length > 0,
        message: "Please select recipients"
      })
  })
});

const formatTime = (hour, min) => {
  let formattedHour = hour;
  let formattedMin = min;
  if (formattedMin < 10) formattedMin = `0${formattedMin}`;
  if (formattedHour < 10) formattedHour = `0${formattedHour}`;
  return `${formattedHour}:${formattedMin}`;
};

const WEEKDAYS_IDX = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ModalNotification = ({visible, setVisible, handleSave, setManage, target, freq, type}) => {
  const isMounted = useMountedState();

  const {roles} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);
  const {facility} = useContext(FacilityNavContext);

  const {api: apiFieldTags} = useApi("field-tags");
  const {api: apiFacilityStates} = useApi("facility-states");
  const {api: apiFacilityUser} = useApi("facility-users");
  const {api: apiChecksheets} = useApi("checksheets");

  const [fieldTags, setFieldTags] = useState();
  const [results, setResults] = useState([]);
  const [checksheetOptions, setChecksheetOptions] = useState(undefined);
  const [states, setStates] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [showExpandedPreview, setShowExpandedPreview] = useState(false);
  // State (not ref) so the form mounts after async options load — a ref alone would not re-render.
  const [initialized, setInitialized] = useState(false);
  // Skip one auto-fill after hydrating a saved include-list (empty match must not select-all).
  const suppressFilterAutoFill = useRef(false);
  const seededPreviewFields = useRef(false);

  const form = useForm({defaultValues, resolver: yupResolver(schema)});
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    handleSubmit,
    formState: {errors, isSubmitted}
  } = form;
  const watchType = watch("notification.type");
  const watchIncludeTags = watch("notification.includeTags");
  const watchTags = watch("notification.tags");
  const watchFilterChecksheets = watch("notification.filterChecksheets");
  const watchChecksheets = watch("notification.checksheets");
  const watchFrequency = watch("notification.frequency");
  const watchState = watch("notification.state");
  const watchRecipients = watch("notification.recipients");

  const prevFrequency = usePrevious(watchFrequency);
  const prevFilterChecksheets = usePrevious(watchFilterChecksheets);

  const includedChecksheetIds = useMemo(
    () => new Set(watchChecksheets?.map(({id}) => id) || []),
    [watchChecksheets]
  );

  // Seed type/state/freq immediately so edit preview does not wait on options hydrate.
  // Do not seed filterChecksheets — that removes the false→true edge hydrate needs to
  // apply suppressFilterAutoFill and restore the saved include-list into the form.
  useEffect(() => {
    if (!isMounted() || seededPreviewFields.current || !type) return;

    const {type: initialType, state: initialState} = parseNotificationTypeKey(type);
    if (initialType) setValue("notification.type", initialType);
    if (initialState) setValue("notification.state", initialState.toLowerCase());
    if (freq && freq.toLowerCase() !== "none")
      setValue("notification.frequency", freq.toLowerCase());
    if (target?.tags?.length) setValue("notification.includeTags", true);

    seededPreviewFields.current = true;
  }, [isMounted, type, freq, target, setValue]);

  const previewChecksheetIds = useMemo(() => {
    if (!hasChecksheetFilter(watchType)) return null;
    if (watchFilterChecksheets) {
      if (watchChecksheets?.length) return watchChecksheets.map(({id}) => id);
      if (target?.checksheetIds?.length) return target.checksheetIds.map(Number);
      return [];
    }
    // Pre-hydrate edit: still preview the saved filter before the form turns it on.
    if (!initialized && target?.checksheetIds?.length) return target.checksheetIds.map(Number);
    return null;
  }, [watchType, watchFilterChecksheets, watchChecksheets, target, initialized]);

  const previewFilterChecksheets =
    !!watchFilterChecksheets || (!initialized && !!target?.checksheetIds?.length);

  const {preview, previewLoading, previewFailed, previewReady, previewSubject, emptyPreviewKind} =
    useNotificationEmailPreview({
      facilityId: facility?.id,
      type: watchType,
      frequency: watchFrequency || null,
      state: watchState || null,
      includeTags: !!watchIncludeTags,
      filterChecksheets: previewFilterChecksheets,
      checksheetIds: previewChecksheetIds
    });

  // Form values when the last save error was shown; any edit away from them clears it.
  const saveErrorValues = useRef(null);

  const showSaveError = message => {
    saveErrorValues.current = JSON.stringify(getValues("notification"));
    setError("notification.save", {type: "server", message});
  };

  const onSubmit = async data => {
    clearErrors("notification.save");
    saveErrorValues.current = null;
    let result;
    try {
      result = await handleSave(data);
    } catch {
      showSaveError("Failed to save notification.");
      return;
    }
    if (typeof result === "string" && result && !isCanceled(result)) showSaveError(result);
  };

  // Backup for validation errors on fields with no visible error, so Save is never silent.
  const onInvalid = formErrors => {
    const shownFields = visibleErrorFields(getValues("notification.type"));
    const hidden = Object.keys(formErrors.notification || {}).filter(
      field => !shownFields.includes(field)
    );
    if (hidden.length)
      showSaveError("Some required information is missing. Check the form and try again.");
  };

  // Compare against a snapshot: inputs like InputTime re-set unchanged values on submit.
  useEffect(() => {
    const subscription = watch(({notification}) => {
      if (saveErrorValues.current && JSON.stringify(notification) !== saveErrorValues.current) {
        saveErrorValues.current = null;
        clearErrors("notification.save");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const toggleChecksheet = sheet => {
    const selected = watchChecksheets || [];
    const updated = includedChecksheetIds.has(sheet.id)
      ? selected.filter(curr => curr.id !== sheet.id)
      : [...selected, sheet];
    // Re-validate after a submit like registered inputs do, so the empty-filter error clears.
    setValue("notification.checksheets", updated, {shouldValidate: isSubmitted});
  };

  const hasRoleRecipientsEnabled = useMemo(() => {
    if (roles?.length > 0 && settings?.roleRecipients === "true") return true;
    return false;
  }, [roles, settings]);

  const listIsRole = useMemo(() => {
    const {recipients} = target || {};
    const [first] = recipients ? Object.values(recipients) : [];
    return first?.isRole;
  }, [target]);

  const handleSearchUser = query => {
    const filter = {
      Omit: watchRecipients?.map(user => user.id),
      showHidden: true,
      State: ["Disabled", "Active"]
    };

    if (query) filter.Search = query;

    const params = {
      facilityId: facility?.id,
      limit: query ? 5 : 20,
      filter: JSON.stringify(filter),
      noTest: true
    };

    apiFacilityUser.callGet("", params).then(({status, data}) => {
      if (status === 200 && data) setUsers(data?.map(user => ({...user, id: user.publicId})) || []);
    });
  };

  const handleSearchRole = query => {
    if (query)
      setUsers(
        roles?.filter(
          role =>
            !watchRecipients.map(({id}) => id).includes(role.id) &&
            role.label.toLowerCase().includes(query?.toLowerCase())
        ) || []
      );
    else
      setUsers(roles?.filter(role => !watchRecipients.map(({id}) => id).includes(role.id)) || []);
  };

  // Initial Load
  useEffect(() => {
    if (isMounted()) {
      if (!fieldTags)
        apiFieldTags.callGet().then(({status, data}) => {
          if (status === 200 && data)
            setFieldTags(data.map(({id, name}) => ({id, name, label: name.toUpperCase()})));
        });

      if (states === undefined)
        apiFacilityStates.callGet().then(response => {
          if (isCanceled(response)) return;
          const {status, data} = response;
          if (status === 200 && data?.length > 0)
            setStates(data.map(({id, name}) => ({id, name, label: name.toUpperCase()})));
          else setStates(null);
        });

      // Facility-scoped sheets for Overdue / Outstanding include-lists (skip optional)
      if (facility?.id && checksheetOptions === undefined)
        apiChecksheets
          .callGet(null, {facilityId: facility.id, includeDrafts: false, withCurrentState: true})
          .then(response => {
            // A superseded request must not mark options loaded, or edit hydrates against [].
            if (isCanceled(response)) return;
            const {status, data} = response;
            if (status === 200 && Array.isArray(data))
              setChecksheetOptions(
                data.filter(isNotificationChecksheet).map(formatChecksheetOption)
              );
            else setChecksheetOptions([]);
          });
    }
  }, [
    isMounted,
    apiFieldTags,
    fieldTags,
    apiFacilityStates,
    states,
    apiChecksheets,
    facility,
    checksheetOptions
  ]);

  useEffect(() => {
    if (
      isMounted() &&
      fieldTags &&
      states !== undefined &&
      checksheetOptions !== undefined &&
      !initialized
    ) {
      if (target && freq) {
        const {times, recipients} = target;
        const freqNormalized = freq.toLowerCase();
        let occurrenceMapper = null;

        if (freqNormalized === "daily") {
          occurrenceMapper = time => ({time: formatTime(time.hour, time.min)});
        } else if (freqNormalized === "weekly") {
          occurrenceMapper = time => {
            const dayOfWeek = WEEKDAYS_IDX[time.dayOfWeek];

            return {
              time: formatTime(time.hour, time.min),
              dayOfWeek: [dayOfWeek]
            };
          };
        } else if (freqNormalized === "monthly") {
          occurrenceMapper = time => ({
            time: formatTime(time.hour, time.min),
            dayOfMonth: time.dayOfMonth
          });
        } else if (freqNormalized === "annual") {
          occurrenceMapper = time => {
            let now = dayjs();
            now = now.month(time.month - 1);
            now = now.date(time.dayOfMonth);
            return {
              dayOfYear: formatDate(now),
              time: formatTime(time.hour, time.min)
            };
          };
        }

        const {type: initialType, state: initialState} = parseNotificationTypeKey(type);

        const notification = {
          type: initialType,
          frequency: freqNormalized !== "none" ? freqNormalized : null,
          occurrences: times ? times.map(occurrenceMapper) : null,
          recipients: listIsRole === hasRoleRecipientsEnabled ? Object.values(recipients) : [],
          filterChecksheets: false,
          checksheets: []
        };

        if (initialState) notification.state = initialState.toLowerCase();

        if (target?.tags) {
          notification.includeTags = target.tags.length > 0;
          notification.tags = fieldTags.filter(tag => target.tags.includes(tag.id));
        }

        // Saved include-list → enable filter UI and restore selected sheets. A legacy empty
        // list ("none") also opens filtered, so saving can't silently widen it to all sheets.
        if (Array.isArray(target?.checksheetIds)) {
          suppressFilterAutoFill.current = true;
          notification.filterChecksheets = true;
          notification.checksheets = checksheetOptions.filter(sheet =>
            target.checksheetIds.map(Number).includes(Number(sheet.id))
          );
        }

        setValue("notification", notification);
      } else if (type) {
        // Create flow opened with a preselected type (e.g. tests / deep link).
        setValue("notification.type", type);
      }

      setInitialized(true);
    }
  }, [
    isMounted,
    states,
    fieldTags,
    checksheetOptions,
    target,
    initialized,
    freq,
    type,
    listIsRole,
    hasRoleRecipientsEnabled,
    setValue
  ]);

  // Reinitialize Occurrences
  useEffect(() => {
    if (isMounted() && initialized && prevFrequency && prevFrequency !== watchFrequency)
      setValue("notification.occurrences", []);
  }, [isMounted, initialized, prevFrequency, watchFrequency, setValue]);

  // Filter toggle: on → start with all facility sheets; off → clear (all sheets in email)
  useEffect(() => {
    if (!isMounted() || !initialized || prevFilterChecksheets === watchFilterChecksheets) return;

    if (suppressFilterAutoFill.current) {
      suppressFilterAutoFill.current = false;
      return;
    }

    if (watchFilterChecksheets) {
      if (!watchChecksheets?.length && checksheetOptions?.length)
        setValue("notification.checksheets", [...checksheetOptions]);
    } else setValue("notification.checksheets", []);
  }, [
    isMounted,
    initialized,
    prevFilterChecksheets,
    watchFilterChecksheets,
    watchChecksheets,
    checksheetOptions,
    setValue
  ]);

  // fieldTags uses [] as "loaded empty"; treat any array as ready.
  const optionsReady =
    Array.isArray(fieldTags) && states !== undefined && checksheetOptions !== undefined;

  const getConfigMismatchWarning = () => {
    if (
      listIsRole === undefined ||
      listIsRole === hasRoleRecipientsEnabled ||
      watchRecipients?.length > 0
    )
      return "";

    return (
      <WarningWrapper>
        <Warning>Warning:</Warning>
        Role recipients is {listIsRole ? "disabled" : "enabled"}. Please reassign notification group
        to a list of roles.
      </WarningWrapper>
    );
  };

  // Match ModalViewAllRecords: swap parent ↔ child instead of stacking modals.
  if (showExpandedPreview && facility?.id)
    return (
      <ModalNotificationEmailPreview
        visible={showExpandedPreview}
        setVisible={open => {
          if (!open) setShowExpandedPreview(false);
        }}
        goBack={() => setShowExpandedPreview(false)}
        facilityId={facility.id}
        type={watchType}
        frequency={watchFrequency || null}
        state={watchState || null}
        includeTags={!!watchIncludeTags}
        filterChecksheets={previewFilterChecksheets}
        checksheetIds={previewChecksheetIds}
        watchTags={watchTags}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible} maxWidth="1100px" width="95%">
      <Heading>Configure Notification</Heading>
      <FormProvider {...form}>
        <ModalBody $split={1}>
          <FormColumn>
            {initialized && optionsReady ? (
              <Form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
                <InputSelect
                  name="notification.type"
                  label="Type"
                  placeholder="Select type..."
                  options={
                    states
                      ? NOTIFICATION_TYPES
                      : NOTIFICATION_TYPES.filter(({value}) => !value.includes("State"))
                  }
                  required
                />

                {watchType && NOTIFICATION_DESC[watchType] && (
                  <Description>{NOTIFICATION_DESC[watchType]}</Description>
                )}

                {(watchType === "State" || watchType === "StateRemind") && (
                  <FormField>
                    <InputRadioGroup
                      name="notification.state"
                      label="Target State"
                      options={states}
                      required
                    />
                  </FormField>
                )}

                {watchType === "Completed" && (
                  <>
                    <InputCheck name="notification.includeTags">
                      Do you want to include tagged fields&nbsp;
                      <Help>
                        All fields with specified tags will be shown at the top of this
                        notification.
                      </Help>
                    </InputCheck>
                    {watchIncludeTags && (
                      <>
                        <SearchWrapper>
                          {fieldTags?.length > 0 ? (
                            <SearchSelect
                              placeholder="Find tags.."
                              results={results}
                              setResults={setResults}
                              search={query => {
                                if (!query) setResults([]);
                                else
                                  setResults(
                                    fieldTags.filter(tag =>
                                      tag.name.toLowerCase().includes(query.toLowerCase())
                                    )
                                  );
                              }}
                              add={selected => {
                                if (!watchTags) setValue("notification.tags", [{...selected}]);
                                else if (!watchTags.map(({id}) => id).includes(selected.id))
                                  setValue("notification.tags", [...watchTags, {...selected}]);
                              }}
                              showAll
                            />
                          ) : (
                            <Text>
                              No field tags exist. Please configure on checksheets to include.
                            </Text>
                          )}
                          <InputError name="notification.tags" errors={errors} />
                        </SearchWrapper>
                        <Inline>
                          {watchTags?.map(({id, name}) => (
                            <Button
                              key={`tag-${id}`}
                              onClick={() =>
                                setValue(
                                  "notification.tags",
                                  watchTags.filter(curr => {
                                    if (curr.id !== id) return true;
                                    return false;
                                  })
                                )
                              }
                              quiet>
                              {name.toUpperCase()}&nbsp;
                              <FontAwesomeIcon icon={faClose} />
                            </Button>
                          ))}
                        </Inline>
                      </>
                    )}
                  </>
                )}

                {hasFrequency(watchType) && (
                  <>
                    <InputSelect
                      name="notification.frequency"
                      label="SEND"
                      placeholder="Select frequency..."
                      options={FREQUENCY_OPTIONS}
                      required
                    />
                    {watchFrequency && (
                      <FormFieldWrapper>
                        <InputNotification type={watchFrequency} name="notification" />
                        <InputError
                          atBottom={false}
                          errors={errors}
                          name="notification.occurrences"
                        />
                      </FormFieldWrapper>
                    )}
                  </>
                )}

                {watchType && (
                  <>
                    <SearchWrapper>
                      <SearchSelect
                        label="RECIPIENTS *"
                        placeholder={`Send to ${hasRoleRecipientsEnabled ? "roles" : "users"}...`}
                        search={hasRoleRecipientsEnabled ? handleSearchRole : handleSearchUser}
                        results={users}
                        setResults={setUsers}
                        add={selected => {
                          if (!watchRecipients)
                            setValue("notification.recipients", [
                              {selected, isRole: hasRoleRecipientsEnabled}
                            ]);
                          else if (!watchRecipients.map(({id}) => id).includes(selected.id))
                            setValue("notification.recipients", [
                              ...watchRecipients,
                              {...selected, isRole: hasRoleRecipientsEnabled}
                            ]);
                        }}
                        showAll
                      />
                    </SearchWrapper>
                    <Recipients fullWidth>
                      {watchRecipients?.map(({label, id, email, isRole}) => (
                        <Button
                          quiet
                          key={`${isRole ? "role-" : "email-"}${id}`}
                          onClick={() =>
                            setValue(
                              "notification.recipients",
                              watchRecipients.filter(curr => {
                                if (curr.id !== id) return true;
                                return false;
                              })
                            )
                          }>
                          {isRole ? label : email}&nbsp;
                          <FontAwesomeIcon icon={faClose} />
                        </Button>
                      ))}
                    </Recipients>
                    <InputError
                      atBottom={false}
                      errors={watchRecipients && watchRecipients.length ? {} : errors}
                      name="notification.recipients"
                    />
                    {getConfigMismatchWarning()}
                  </>
                )}

                {hasChecksheetFilter(watchType) && (
                  <>
                    <InputCheck name="notification.filterChecksheets">
                      Filter checksheets&nbsp;
                      <Help>
                        Leave unchecked to include all scheduled checksheets. When checked, click a
                        sheet to include (check) or exclude (x) it from this group&apos;s Overdue /
                        Outstanding email. Optional sheets are omitted — they do not appear in these
                        emails. External (Client) sheets are labeled.
                      </Help>
                    </InputCheck>
                    {watchFilterChecksheets &&
                      (checksheetOptions?.length > 0 ? (
                        <ChecksheetPills wrap fullWidth>
                          {checksheetOptions.map(sheet => {
                            const included = includedChecksheetIds.has(sheet.id);
                            return (
                              <ChecksheetPill
                                key={`checksheet-${sheet.id}`}
                                type="button"
                                onClick={() => toggleChecksheet(sheet)}
                                quiet
                                included={included ? 1 : 0}>
                                {sheet.label}&nbsp;
                                <FontAwesomeIcon icon={included ? faCheck : faClose} />
                              </ChecksheetPill>
                            );
                          })}
                        </ChecksheetPills>
                      ) : (
                        <Text>No scheduled checksheets available for this facility.</Text>
                      ))}
                    <InputError
                      testId="notification-checksheets"
                      atBottom={false}
                      errors={errors}
                      name="notification.checksheets"
                    />
                  </>
                )}

                <InputError
                  testId="notification-save"
                  atBottom={false}
                  errors={errors}
                  name="notification.save"
                />
                <ButtonWrapper>
                  <Button type="submit">Save</Button>
                  <Button type="button" onClick={() => setManage(false)}>
                    Cancel
                  </Button>
                </ButtonWrapper>
              </Form>
            ) : (
              <NotLoaded>
                <Loader />
              </NotLoaded>
            )}
          </FormColumn>

          <NotificationEmailPreview
            preview={preview}
            previewLoading={previewLoading}
            previewFailed={previewFailed}
            previewReady={previewReady}
            previewSubject={previewSubject}
            emptyPreviewKind={emptyPreviewKind}
            watchType={watchType}
            watchFilterChecksheets={watchFilterChecksheets}
            watchChecksheets={watchChecksheets}
            watchIncludeTags={watchIncludeTags}
            watchTags={watchTags}
            onExpand={() => setShowExpandedPreview(true)}
          />
        </ModalBody>
      </FormProvider>
    </Modal>
  );
};

ModalNotification.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  setManage: PropTypes.func.isRequired,
  target: PropTypes.objectOf(PropTypes.any),
  freq: PropTypes.string,
  type: PropTypes.string
};

// Style Overrides
const Description = styled(Small)`
  margin-top: ${pad}px;
`;

const Recipients = styled(Inline)`
  flex-wrap: wrap;
`;

const ChecksheetPills = styled(Inline)`
  margin: ${pad / 2}px 0 ${pad}px;
  max-width: 100%;
`;

const ChecksheetPill = styled(Button)`
  opacity: ${({included}) => (included ? 1 : 0.45)};
`;

const SearchWrapper = styled.div`
  position: relative;
  margin: ${pad}px 0;
`;

const WarningWrapper = styled.p`
  margin-top: ${pad / 2}px;
  ${voice.quiet};
`;

const Warning = styled.strong`
  color: ${({theme}) => theme.warning};
`;

const ButtonWrapper = styled(Inline)`
  margin-top: ${pad * 3}px;
`;

const ModalBody = styled.div.withConfig({
  shouldForwardProp: prop => prop !== "$split"
})`
  display: flex;
  flex-direction: column;
  gap: ${pad * 2}px;

  ${bp(4)} {
    flex-direction: ${({$split}) => ($split ? "row" : "column")};
    align-items: flex-start;
  }
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

export default ModalNotification;
